import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { configService, StoreConfigResponse, StoreInactiveError, StoreNotFoundError, HeaderOverlayConfig, CustomScreen, ProductDetailSection, KycStatus } from '../services/config.service';
import { appConfigStore } from '../app/stores/AppConfig';
import { navigationStore } from '../app/stores/Navigation';
import { ScreenComponent } from '../models/enums/ScreenComponents';
import {
    CompanyName,
    AppHeaderName,
    FooterAppName,
    AppLogo as AppLogoFallback,
    CURRENCY,
    VAT_PERCENTAGE,
    DEFAULT_PRIMARY_COLOR
} from '../config/constants';
import { ThemeMode } from '../utils/themeUtils';

// Default header overlay configuration
const DEFAULT_HEADER_OVERLAY: HeaderOverlayConfig = {
    enabled: false,
    text: '',
    font: null,
    fontSize: 3,
    fontWeight: 700,
    color: '#FFFFFF',
    position: { x: 50, y: 50 },
    textAlign: 'center',
    textShadow: {
        enabled: true,
        color: 'rgba(0,0,0,0.5)',
        blur: 10,
        offsetX: 0,
        offsetY: 4
    },
    letterSpacing: 2,
    vignette: {
        enabled: true,
        color: '#000000',
        intensity: 0.4
    }
};

// Normalized config shape (after middleware processing)
export interface NormalizedStoreConfig {
    companyName: string;
    appHeaderName: string;
    footerAppName: string;
    footerDescription: string;
    appLogo: string;
    appHeaderImage: string | null;
    appHeaderMediaType: 'image' | 'video';
    appHeaderPosition: { x: number; y: number };
    appHeaderScale: number;
    appHeaderObjectFit: 'cover' | 'contain' | 'fill';
    headerOverlay: HeaderOverlayConfig;
    splashLogo: string | null;
    splashTintColor: string | null;
    splashLogoTintColor: string | null;
    sharingImageSquare: string | null;
    sharingImageLandscape: string | null;
    currency: string;
    vatPercentage: number;
    primaryColor: string;
    themeMode: ThemeMode;
    primaryFont: string | null;
    secondaryFont: string | null;
    logoFont: string | null;
    socialFacebook: string;
    socialInstagram: string;
    socialTwitter: string;
    socialLinkedin: string;
    socialTiktok: string;
    storeId: string | null;
    storeName: string | null;
    subdomain: string | null;
    productDetailSections: ProductDetailSection[];
}

// State shape
interface StoreConfigState {
    config: NormalizedStoreConfig;
    isLoading: boolean;
    error: string | null;
    isInactive: boolean;
    inactiveStoreName?: string;
    inactivePrimaryColor?: string;
    isNotFound: boolean;
    notFoundSubdomain?: string;
    kycStatus: KycStatus;
    kycResolved: boolean;
}

// Action types
type StoreConfigAction =
    | { type: 'LOAD_START' }
    | { type: 'LOAD_SUCCESS'; payload: { config: NormalizedStoreConfig; kycStatus: KycStatus; fromApi?: boolean } }
    | { type: 'LOAD_ERROR'; payload: string }
    | { type: 'STORE_INACTIVE'; payload: { storeName: string; primaryColor?: string } }
    | { type: 'STORE_NOT_FOUND'; payload: { subdomain: string } }
    | { type: 'RELOAD' };

// Context type
interface StoreConfigContextType {
    state: StoreConfigState;
    config: NormalizedStoreConfig; // Alias for easier access
    isLoading: boolean;
    isInactive: boolean;
    inactiveStoreName?: string;
    inactivePrimaryColor?: string;
    isNotFound: boolean;
    notFoundSubdomain?: string;
    companyName: string;
    kycStatus: KycStatus;
    kycResolved: boolean;
    loadConfig: () => Promise<void>;
    reloadConfig: () => Promise<void>;
}

// Default navigation items with sort order (used when no custom screens are configured)
const DEFAULT_NAV_BAR_ITEMS: { screen: ScreenComponent; sortOrder: number }[] = [
    { screen: ScreenComponent.Shop, sortOrder: 1 },
    { screen: ScreenComponent.Orders, sortOrder: 2 },
];

const DEFAULT_DRAWER_ITEMS: { screen: ScreenComponent; sortOrder: number }[] = [
    { screen: ScreenComponent.Shop, sortOrder: 1 },
    { screen: ScreenComponent.Orders, sortOrder: 2 },
    { screen: ScreenComponent.Cart, sortOrder: 98 },
    { screen: ScreenComponent.Login, sortOrder: 99 },
];

// Base screens that are always available in the navigator (not persisted, loaded fresh)
const BASE_SCREEN_LIST: ScreenComponent[] = [
    ScreenComponent.Shop,
    ScreenComponent.Product,
    ScreenComponent.Orders,
    ScreenComponent.Contact,
    ScreenComponent.Cart,
    ScreenComponent.Portal,
    ScreenComponent.ThemePreview,
];

// Apply screen configuration from API to navigation store
// Respects sort order from the database for proper ordering
function applyCustomScreens(screens?: CustomScreen[]) {
    if (!screens || screens.length === 0) {
        const defaultNavBar = DEFAULT_NAV_BAR_ITEMS
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(item => item.screen);
        const defaultDrawer = DEFAULT_DRAWER_ITEMS
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(item => item.screen);
        navigationStore.getState().updateNavBarItemList(defaultNavBar);
        navigationStore.getState().updateDrawerItemList(defaultDrawer);
        // Reset to base screen list when no custom screens
        navigationStore.getState().updatescreenList(BASE_SCREEN_LIST);
        return;
    }

    // Get configured screen keys for quick lookup
    const configuredScreenKeys = new Set(screens.map(s => s.screenKey));

    // Build the full screen list: base screens + custom screens from API
    // Note: API already filters for isActive=true, so all returned screens are active
    const fullScreenList = [...BASE_SCREEN_LIST];
    screens
        .filter(screen => Object.values(ScreenComponent).includes(screen.screenKey as ScreenComponent))
        .forEach(screen => {
            const screenEnum = screen.screenKey as ScreenComponent;
            if (!fullScreenList.includes(screenEnum)) {
                fullScreenList.push(screenEnum);
            }
        });

    // Build NavBar items from configured screens + defaults for unconfigured core screens
    const navBarScreens: { screen: ScreenComponent; sortOrder: number }[] = [];
    
    // Add configured screens that should show in NavBar
    screens
        .filter(screen => screen.showInNavBar && Object.values(ScreenComponent).includes(screen.screenKey as ScreenComponent))
        .forEach(screen => {
            navBarScreens.push({
                screen: screen.screenKey as ScreenComponent,
                sortOrder: screen.sortOrder,
            });
        });

    // Add default NavBar items that aren't configured (fallback for Shop/Orders if not in DB)
    DEFAULT_NAV_BAR_ITEMS.forEach(defaultItem => {
        if (!configuredScreenKeys.has(defaultItem.screen)) {
            navBarScreens.push(defaultItem);
        }
    });

    // Build Drawer items from configured screens + defaults for unconfigured core screens
    const drawerScreens: { screen: ScreenComponent; sortOrder: number }[] = [];
    
    // Add configured screens that should show in Drawer
    screens
        .filter(screen => screen.showInDrawer && Object.values(ScreenComponent).includes(screen.screenKey as ScreenComponent))
        .forEach(screen => {
            drawerScreens.push({
                screen: screen.screenKey as ScreenComponent,
                sortOrder: screen.sortOrder,
            });
        });

    // Add default Drawer items that aren't configured
    DEFAULT_DRAWER_ITEMS.forEach(defaultItem => {
        if (!configuredScreenKeys.has(defaultItem.screen)) {
            drawerScreens.push(defaultItem);
        }
    });

    // Sort by sortOrder and extract just the screen components
    const finalNavBarItems = navBarScreens
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(item => item.screen);
    
    const finalDrawerItems = drawerScreens
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(item => item.screen);

    // Build display names map from API screens
    const displayNamesMap: Record<string, string> = {};
    screens.forEach(screen => {
        if (screen.displayName) {
            displayNamesMap[screen.screenKey] = screen.displayName;
        }
    });

    // Build routes map from API screens (for custom URL paths)
    const routesMap: Record<string, string> = {};
    screens.forEach(screen => {
        if (screen.route) {
            routesMap[screen.screenKey] = screen.route;
        }
    });

    // Build hero configs map from API screens (for video/image backgrounds)
    const heroConfigsMap: Record<string, { heroVideoUrl?: string | null; heroImageUrl?: string | null; heroOverlayOpacity?: number | null }> = {};
    screens.forEach(screen => {
        if (screen.heroVideoUrl || screen.heroImageUrl) {
            heroConfigsMap[screen.screenKey] = {
                heroVideoUrl: screen.heroVideoUrl,
                heroImageUrl: screen.heroImageUrl,
                heroOverlayOpacity: screen.heroOverlayOpacity ?? 0.4,
            };
        }
    });

    // Update all navigation lists
    navigationStore.getState().updatescreenList(fullScreenList);
    navigationStore.getState().updateNavBarItemList(finalNavBarItems);
    navigationStore.getState().updateDrawerItemList(finalDrawerItems);
    navigationStore.getState().updateScreenDisplayNames(displayNamesMap);
    navigationStore.getState().updateScreenRoutes(routesMap);
    navigationStore.getState().updateScreenHeroConfigs(heroConfigsMap);
}

// Middleware - Normalizes API response before it enters state
// Uses cachedTheme from appConfigStore as intermediate fallback for visual properties
// Priority: API response > cached theme > hardcoded defaults
function normalizeConfig(response: StoreConfigResponse | null): NormalizedStoreConfig {
    // Get cached theme for fallback (prevents visual flash on page refresh)
    const cachedTheme = appConfigStore.getState().cachedTheme;
    
    // Add cache-busting to header media URL to ensure fresh content after uploads
    let headerImageUrl = response?.config.appHeaderImage || null;
    if (headerImageUrl) {
        // Add timestamp for cache busting, updating every minute to balance freshness vs caching
        const cacheBuster = Math.floor(Date.now() / 60000);
        const separator = headerImageUrl.includes('?') ? '&' : '?';
        headerImageUrl = `${headerImageUrl}${separator}v=${cacheBuster}`;
    }
    
    const normalized = {
        // Use cached values for visual properties to prevent flash
        companyName: response?.config.companyName || cachedTheme?.companyName || CompanyName,
        appHeaderName: response?.config.appHeaderName || cachedTheme?.appHeaderName || AppHeaderName,
        footerAppName: response?.config.footerAppName || FooterAppName,
        footerDescription: response?.config.footerDescription || 'Bring your quality products to the world',
        appLogo: response?.config.appLogo || cachedTheme?.appLogo || AppLogoFallback,
        appHeaderImage: headerImageUrl,
        appHeaderMediaType: response?.config.appHeaderMediaType || 'image',
        appHeaderPosition: response?.config.appHeaderPosition || { x: 50, y: 50 },
        appHeaderScale: response?.config.appHeaderScale || 1.0,
        appHeaderObjectFit: response?.config.appHeaderObjectFit || 'cover',
        // Header overlay: merge with defaults to ensure all properties exist
        headerOverlay: {
            ...DEFAULT_HEADER_OVERLAY,
            ...(response?.config.headerOverlay || {})
        },
        // Splash screen settings
        splashLogo: response?.config.splashLogo || null,
        splashTintColor: response?.config.splashTintColor || null,
        splashLogoTintColor: response?.config.splashLogoTintColor || null,
        sharingImageSquare: response?.config.sharingImageSquare || response?.config.appLogo || AppLogoFallback,
        sharingImageLandscape: response?.config.sharingImageLandscape || null,
        currency: response?.config.currency || CURRENCY,
        vatPercentage: response?.config.vatPercentage || VAT_PERCENTAGE,
        // Theme properties: API > cached > default (prevents color flash)
        primaryColor: response?.config.primaryColor || cachedTheme?.primaryColor || DEFAULT_PRIMARY_COLOR,
        themeMode: response?.config.themeMode || cachedTheme?.themeMode || 'light',
        // Fonts: API > cached > null (null = use local fonts)
        primaryFont: response?.config.primaryFont ?? cachedTheme?.primaryFont ?? null,
        secondaryFont: response?.config.secondaryFont ?? cachedTheme?.secondaryFont ?? null,
        logoFont: response?.config.logoFont ?? cachedTheme?.logoFont ?? null,
        socialFacebook: response?.config.socialFacebook || 'https://facebook.com',
        socialInstagram: response?.config.socialInstagram || 'https://instagram.com',
        socialTwitter: response?.config.socialTwitter || 'https://twitter.com',
        socialLinkedin: response?.config.socialLinkedin || 'https://linkedin.com',
        socialTiktok: response?.config.socialTiktok || 'https://tiktok.com',
        storeId: response?.store.id || null,
        storeName: response?.store.name || null,
        subdomain: response?.store.subdomain || null,
        // Product Detail Sections - default sections if not configured
        productDetailSections: response?.config.productDetailSections?.sections || [
            {
                id: 'shipping',
                title: 'Shipping & Returns',
                content: '<p><strong>Free Shipping</strong> on all orders</p><p><strong>Free Returns</strong> within 30 days of purchase</p><p>Members earn 25 Preferred Points on this purchase. Learn More</p>',
                isEnabled: true,
                sortOrder: 1,
                isDefault: true
            },
            {
                id: 'care',
                title: 'Care Instructions',
                content: '<p>• Machine wash cold with like colors</p><p>• Do not bleach</p><p>• Tumble dry low</p><p>• Warm iron if needed</p>',
                isEnabled: true,
                sortOrder: 2,
                isDefault: true
            }
        ]
    };
    return normalized;
}

// Initial state - Starts with fallback values from constants
const initialState: StoreConfigState = {
    config: normalizeConfig(null), // Uses fallbacks
    isLoading: true,
    error: null,
    isInactive: false,
    isNotFound: false,
    kycStatus: 'pending',
    kycResolved: false,
};

// Reducer
function storeConfigReducer(state: StoreConfigState, action: StoreConfigAction): StoreConfigState {
    switch (action.type) {
        case 'LOAD_START':
            return {
                ...state,
                isLoading: true,
                error: null,
                isInactive: false,
                isNotFound: false,
            };
        case 'LOAD_SUCCESS':
            return {
                config: action.payload.config, 
                isLoading: false,
                error: null,
                isInactive: false,
                isNotFound: false,
                kycStatus: action.payload.kycStatus,
                kycResolved: action.payload.fromApi ? true : state.kycResolved,
            };
        case 'LOAD_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };
        case 'STORE_INACTIVE':
            return {
                ...state,
                isLoading: false,
                isInactive: true,
                inactiveStoreName: action.payload.storeName,
                inactivePrimaryColor: action.payload.primaryColor,
                isNotFound: false,
                error: null,
            };
        case 'STORE_NOT_FOUND':
            return {
                ...state,
                isLoading: false,
                isNotFound: true,
                notFoundSubdomain: action.payload.subdomain,
                isInactive: false,
                error: null,
            };
        case 'RELOAD':
            return {
                ...state,
                isLoading: true,
                error: null,
                isInactive: false,
                isNotFound: false,
            };
        default:
            return state;
    }
}

const StoreConfigContext = createContext<StoreConfigContextType | undefined>(undefined);

export const StoreConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(storeConfigReducer, initialState);
    const {
        storeConfig: persistedConfig,
        isConfigHydrated: isHydrated,
        setStoreConfig: setPersisted,
        clearStoreConfig: clearPersisted
    } = appConfigStore();

    const loadConfig = async () => {
        const startTime = Date.now();
        const MIN_LOADING_TIME = 1000; // Show loading for at least 1 second for smooth UX

        // If we have persisted config and it's been hydrated, use it first
        if (isHydrated && persistedConfig) {
            const normalizedConfig = normalizeConfig(persistedConfig);
            dispatch({ type: 'LOAD_SUCCESS', payload: { config: normalizedConfig, kycStatus: (persistedConfig.store as any).kycStatus || 'pending' } });

            // Apply theme from persisted config
            // Only override darkMode for explicit 'dark' or 'light' modes
            // For 'dynamic' mode, let the user's persisted choice remain
            const themeMode = persistedConfig?.config?.themeMode;
            if (themeMode === 'dark') {
                appConfigStore.getState().setDarkMode(true);
            } else if (themeMode === 'light') {
                appConfigStore.getState().setDarkMode(false);
            }
            // 'dynamic' mode: don't override - user's persisted darkMode is already set
        } else {
            dispatch({ type: 'LOAD_START' });
        }

        try {
            const apiResponse = await configService.loadConfig();
            
            // Ensure minimum loading time for smooth UX
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < MIN_LOADING_TIME) {
                await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME - elapsedTime));
            }

            // Save to persisted store
            setPersisted(apiResponse);

            // Apply theme mode from fresh config
            // Only override darkMode for explicit 'dark' or 'light' modes
            // For 'dynamic' mode, let the user's persisted choice remain
            const themeMode = apiResponse?.config?.themeMode;
            if (themeMode === 'dark') {
                appConfigStore.getState().setDarkMode(true);
            } else if (themeMode === 'light') {
                appConfigStore.getState().setDarkMode(false);
            }
            // 'dynamic' mode: don't override - user's persisted darkMode is already set

            // Middleware: Normalize API response before dispatching to reducer
            const normalizedConfig = normalizeConfig(apiResponse);
            
            // Apply custom screens to navigation store
            applyCustomScreens(apiResponse.screens);
            
            const resolvedKycStatus = (apiResponse.store as any).kycStatus || 'pending';
            dispatch({ type: 'LOAD_SUCCESS', payload: { config: normalizedConfig, kycStatus: resolvedKycStatus, fromApi: true } });
        } catch (err) {
            // If we have persisted config as fallback, don't show error
            if (persistedConfig) {
                console.warn('[StoreConfigContext] ⚠️ Using persisted config as fallback');
                return;
            }

            // Handle store not found specially
            if (err instanceof StoreNotFoundError) {
                dispatch({
                    type: 'STORE_NOT_FOUND',
                    payload: {
                        subdomain: err.subdomain
                    }
                });
                return;
            }

            // Handle inactive store specially
            if (err instanceof StoreInactiveError) {
                dispatch({
                    type: 'STORE_INACTIVE',
                    payload: {
                        storeName: err.storeName,
                        primaryColor: err.primaryColor
                    }
                });
                return;
            }

            const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration';
            dispatch({ type: 'LOAD_ERROR', payload: errorMessage });
            console.error('[StoreConfigContext] ❌ Config loading error:', err);
        }
    };

    const reloadConfig = async () => {
        dispatch({ type: 'RELOAD' });
        configService.clearConfig();
        clearPersisted();
        await loadConfig();
    };

    useEffect(() => {
        // Only load if hydrated (to avoid double loading)
        if (isHydrated) {
            loadConfig();
        }
    }, [isHydrated]);

    return (
        <StoreConfigContext.Provider value={{ 
            state, 
            config: state.config,
            isLoading: state.isLoading,
            isInactive: state.isInactive,
            inactiveStoreName: state.inactiveStoreName,
            inactivePrimaryColor: state.inactivePrimaryColor,
            isNotFound: state.isNotFound,
            notFoundSubdomain: state.notFoundSubdomain,
            companyName: state.config.companyName,
            kycStatus: state.kycStatus,
            kycResolved: state.kycResolved,
            loadConfig, 
            reloadConfig 
        }}>
            {children}
        </StoreConfigContext.Provider>
    );
};

export const useStoreConfig = () => {
    const context = useContext(StoreConfigContext);
    if (context === undefined) {
        throw new Error('useStoreConfig must be used within a StoreConfigProvider');
    }
    return context;
};

